import { moment } from '@seiue/moment'
import { DatePicker } from '@seiue/ui'
import { Form, FormProps, Radio } from 'antd'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import styled from 'styled-components'

import { TaskReq } from 'packages/sdks-next/growp'
import { useFormValues } from 'packages/utils'

import { ConditionSettings } from './ConditionSettings'
// import { faPlus } from '@fortawesome/pro-regular-svg-icons'
// import { getTheme } from '@/utils/theme'
// import { AdministratorModal } from '@/uikit/Administrator/AdministratorModal'
// import { AvatorList } from '@/uikit/Administrator/AvatorList'

export type SubmitValues = Pick<
  TaskReq,
  | 'endAt'
  | 'adminIds'
  | 'startAt'
  | 'timedStartAt'
  | 'prevTaskIds'
  | 'isShow'
  | 'setting'
>

export type CommonBasicSettingsProps = {
  form: FormProps['form']
  value: SubmitValues
  activityTaskOptions: { value: number; label: string }[]
  ConditionMarginLeft?: number
  openTypeDisabled?: boolean
}

export type CommonBasicInfoRef = {
  getBasicInfo: () => SubmitValues
}

/**
 * 创建任务 - 基本信息 - 开启方式&结束方式&显示方式&管理员
 *
 * @param props - 参数
 * @param props.form - 表单实例
 * @param props.value - 任务信息
 * @param props.activityTaskOptions - 前序任务选项
 * @param props.ConditionMarginLeft - 条件开启组件的左边距, 默认为 82
 * @param props.openTypeDisabled - 开启方式是否禁用
 * @returns component
 */
export const CommonBasicInfo = forwardRef<
  CommonBasicInfoRef,
  CommonBasicSettingsProps
>(
  (
    {
      form,
      value,
      ConditionMarginLeft = 82,
      activityTaskOptions,
      openTypeDisabled,
    },
    ref
  ) => {
    // const theme = getTheme()
    // const [visible, setVisible] = useState<boolean>(false)
    const [formValues, setFormValues] = useFormValues<SubmitValues>(value)
    const [adminIds, setAdminIds] = useState<number[]>([])
    const [validCondition, setValidCondition] = useState<boolean>(false)
    useEffect(() => {
      const { prevTaskIds, startAt, timedStartAt, endAt } = value || {}
      const hasTask = prevTaskIds && prevTaskIds.length > 0
      if (timedStartAt || hasTask) {
        form?.setFieldValue('openType', 'condition')
      } else {
        form?.setFieldValue('openType', startAt ? 'open' : 'close')
      }
      setAdminIds(value.adminIds || [])
      form?.setFieldValue('endType', endAt ? 'time' : 'close')
    }, [value])

    useImperativeHandle(ref, () => {
      return {
        getBasicInfo() {
          const openType = form?.getFieldValue('openType')

          const { prevTaskIds, timedStartAt } = formValues
          if (
            openType === 'condition' &&
            !prevTaskIds?.length &&
            !timedStartAt
          ) {
            setValidCondition(true)
            return {}
          }

          return {
            ...formValues,
            adminIds,
          }
        },
      }
    })

    return (
      <>
        <Form.Item
          label={'开启方式'}
          name="openType"
          style={{ marginBottom: 12 }}
        >
          <Radio.Group
            disabled={!!openTypeDisabled}
            defaultValue={'open'}
            options={[
              { label: '默认开启', value: 'open' },
              { label: '条件开启', value: 'condition' },
              { label: '暂不开启', value: 'close' },
            ]}
            onChange={(val) => {
              if (val.target.value !== 'condition') {
                setFormValues('timedStartAt', null)
                setFormValues('prevTaskIds', [])
              } else {
                setValidCondition(false)
              }
              if (val.target.value === 'open') {
                setFormValues('startAt', moment().format('YYYY-MM-DD HH:mm:ss'))
                setFormValues('isShow', true)
              } else {
                setFormValues('startAt', null)
              }
            }}
          />
        </Form.Item>
        <Form.Item
          dependencies={['openType']}
          noStyle={true}
        >
          {({ getFieldValue }) => {
            const openType = getFieldValue('openType')
            return openType === 'condition' ? (
              <ConditionSettings
                timedStartAt={formValues.timedStartAt}
                prevTaskIds={formValues.prevTaskIds}
                setFormValues={setFormValues}
                marginLeft={ConditionMarginLeft}
                activityTaskOptions={activityTaskOptions}
                setValidCondition={setValidCondition}
                validCondition={validCondition}
                openTypeDisabled={!!openTypeDisabled}
              />
            ) : null
          }}
        </Form.Item>
        <Form.Item
          label={'结束方式'}
          name="endType"
          style={{ marginBottom: 12 }}
        >
          <Radio.Group
            options={[
              { label: '暂不设置', value: 'close' },
              { label: '设置截止时间', value: 'time' },
            ]}
            onChange={(val) => {
              if (val.target.value === 'time') {
                setFormValues('endAt', moment().format('YYYY-MM-DD HH:mm:ss'))
              } else {
                setFormValues('endAt', null)
              }
            }}
          />
        </Form.Item>
        <Form.Item
          dependencies={['endType']}
          style={{ marginBottom: 12 }}
          noStyle={true}
        >
          {({ getFieldValue }) => {
            const endType = getFieldValue('endType')
            return endType === 'time' ? (
              <DatePicker
                showTime={true}
                style={{ width: 320, marginLeft: 83, marginBottom: 12 }}
                value={formValues.endAt ? moment(formValues.endAt) : null}
                onChange={(date) => {
                  setFormValues('endAt', date?.format('YYYY-MM-DD HH:mm:ss'))
                }}
              />
            ) : null
          }}
        </Form.Item>
        {form?.getFieldValue('openType') !== 'open' ? (
          <StyleFormItem
            label={'显示方式'}
            labelAlign="right"
            style={{ marginBottom: 12 }}
          >
            <Radio.Group
              options={[
                { label: '显示', value: true },
                { label: '隐藏', value: false },
              ]}
              value={formValues.isShow}
              onChange={(val) => {
                setFormValues('isShow', val.target.value)
              }}
            />
          </StyleFormItem>
        ) : null}
        {/* 暂时隐藏，后续可能会用 */}
        {/* <Form.Item label={'管理员'}>
          <AdminWrapper>
            {adminIds.length > 0 ? <AvatorList adminIds={adminIds} /> : null}
            <ButtonIcon
              icon={faPlus}
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: theme.background._3,
                borderRadius: '50%',
              }}
              onClick={() => setVisible(true)}
            />
          </AdminWrapper>
        </Form.Item> */}
        {/* <AdministratorModal
          adminIds={adminIds}
          setAdminIds={(v) => setAdminIds(v)}
          visible={visible}
          onClose={() => setVisible(false)}
        /> */}
      </>
    )
  }
)

CommonBasicInfo.displayName = 'CommonBasicInfo'

const StyleFormItem = styled(Form.Item)`
  .ant-form-item-label {
    width: 76px;
  }
`

// const AdminWrapper = styled.div`
//   display: flex;
//   justify-content: flex-start;
//   align-items: center;
//   flex-wrap: wrap;
//   gap: 24px;
// `
